import useAlertDialog from "../../../hooks/useAlertDialog";
import { AlertType } from "../../../hooks/common/useAttachAlertDialog";
import { banPartnerAdmin, unbanPartnerAdmin } from "../../../../services/PartnerService";

const MIN_BAN_REASON = 20;
/** Khoá khẩn bỏ qua chốt chặn ⇒ đòi lý do dài hơn, để buộc người bấm phải nghĩ. Khớp backend. */
const MIN_FORCE_BAN_REASON = 30;
const CODE_HAS_LUGGAGE = "PartnerHasLuggageInStorage";

export type BanAttemptResult =
	| { ok: true }
	/** Bị chặn vì trạm còn khách chưa tới ngày lấy đồ — hộp thoại mở bước khoá khẩn */
	| { ok: false; blockedByLuggage: true; ordersInStorage: number | null }
	/** Hỏng vì lý do khác — đã hiện thông báo, hộp thoại không phải làm gì thêm */
	| { ok: false; blockedByLuggage: false };

function readErrorBody(err: unknown) {
	if (err && typeof err === "object" && "response" in err) {
		return (
			err as {
				response?: { data?: { message?: string; code?: string; ordersInStorage?: number } };
			}
		).response?.data;
	}
	return undefined;
}

export default function useBanPartnerAdmin(props: { reload: () => void }) {
	const { openAlertDialog } = useAlertDialog();

	const banPartnerAd = async (
		id: string,
		reason: string,
		force?: boolean,
		forceReason?: string
	): Promise<BanAttemptResult> => {
		const trimmed = typeof reason === "string" ? reason.trim() : "";
		if (trimmed.length < MIN_BAN_REASON) {
			openAlertDialog?.(AlertType.Fail, `Ban reason must be at least ${MIN_BAN_REASON} characters.`);
			return { ok: false, blockedByLuggage: false };
		}

		const trimmedForce = typeof forceReason === "string" ? forceReason.trim() : "";
		if (force && trimmedForce.length < MIN_FORCE_BAN_REASON) {
			openAlertDialog?.(AlertType.Fail, `Lý do khoá khẩn phải có tối thiểu ${MIN_FORCE_BAN_REASON} ký tự.`);
			return { ok: false, blockedByLuggage: false };
		}

		try {
			await banPartnerAdmin(id, trimmed, force, trimmedForce);
			openAlertDialog?.(
				AlertType.Success,
				force ? "Đã khoá khẩn cấp. Lần khoá này đã được ghi nhật ký." : "Station banned.",
				() => {
					props.reload();
				}
			);
			return { ok: true };
		} catch (err: unknown) {
			const body = readErrorBody(err);

			// Bị chặn vì khách còn đồ: KHÔNG hiện thông báo lỗi ở đây — trả về để hộp
			// thoại mở bước khoá khẩn, người bấm đọc được số đơn rồi mới quyết.
			if (body?.code === CODE_HAS_LUGGAGE) {
				return {
					ok: false,
					blockedByLuggage: true,
					ordersInStorage: typeof body.ordersInStorage === "number" ? body.ordersInStorage : null,
				};
			}

			openAlertDialog?.(AlertType.Fail, body?.message || "Ban failed.");
			return { ok: false, blockedByLuggage: false };
		}
	};

	const unbanPartnerAd = async (id: string) => {
		try {
			await unbanPartnerAdmin(id);
			openAlertDialog?.(AlertType.Success, "Station unbanned.", () => {
				props.reload();
			});
		} catch (err: unknown) {
			const body = readErrorBody(err);
			openAlertDialog?.(AlertType.Fail, body?.message || "Unban failed.");
		}
	};

	return {
		banPartnerAd,
		unbanPartnerAd,
		MIN_BAN_REASON,
		MIN_FORCE_BAN_REASON,
	};
}
